import { Component, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    CreateCurrencyConfigurationCommand,
    CurrencyConfigurationDto,
    CurrencyService,
    FEATURE_CURRENCY,
    FlyFreelyError,
    FlyFreelyLoggingService,
    JurisdictionService,
    OperatingCategoryValuesDtoObject,
    OperatingCategoryValuesDtoOperatingCategoryDto,
    operatingConditionsTypeLookup,
    operatingConditionsValueLookup,
    UpdateCurrencyConfigurationCommand,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { FormatMTOWPipe } from '@flyfreely-portal-ui/ui';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface CurrencyConfigFormGroup {
    currencyOperatingConditionIdList: FormControl<number[]>;
    requiredOperatingConditionIdList: FormControl<number[]>;
    days: FormControl<number>;
    requiredFlights: FormControl<number>;
    requiredFlightTimeSeconds: FormControl<number>;
    usingThisAuthority: FormControl<boolean>;
}

@Component({
    selector: 'currency-configuration-dialogue',
    templateUrl: './currency-configuration.component.html'
})
export class CurrencyConfigurationDialogue {
    @Input() airspaceJurisdictionId: number;
    @Input() personnelRegisterId: number;
    @Input() managingOrganisationId: number;

    operatingConditions: {
        type: string;
        value: string;
        id: number;
    }[];
    currencyConfigurations: CurrencyConfigurationDto[];
    editingConfig: number;
    currencyFeatureFlag = [FEATURE_CURRENCY];

    readonly operatingConditionsTypeLookup = operatingConditionsTypeLookup;
    readonly operatingConditionsValueLookup = operatingConditionsValueLookup;

    private workTracker = new WorkTracker();
    working = false;

    private ngUnsubscribe$ = new Subject<void>();

    // the ID of the entry being edited
    editing: number;
    formGroup: FormGroup<{
        add: FormGroup<CurrencyConfigFormGroup>;
        edit: FormGroup<CurrencyConfigFormGroup>;
    }>;

    constructor(
        public modal: BsModalRef<CurrencyConfigurationDialogue>,
        modalOptions: ModalOptions,
        private logging: FlyFreelyLoggingService,
        private currencyService: CurrencyService,
        private jurisdictionService: JurisdictionService,
        private commonDialoguesService: CommonDialoguesService,
        private formatMTOWPipe: FormatMTOWPipe
    ) {
        modalOptions.closeInterceptor = () => {
            if (this.editingConfig != null) {
                this.editingConfig = null;
                return Promise.reject();
            }
            return Promise.resolve();
        };
    }

    ngOnInit() {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.formGroup = new FormGroup({
            add: new FormGroup({
                currencyOperatingConditionIdList: new FormControl(
                    [],
                    [Validators.required]
                ),
                requiredOperatingConditionIdList: new FormControl([]),
                days: new FormControl(undefined),
                requiredFlights: new FormControl(0, [Validators.required]),
                requiredFlightTimeSeconds: new FormControl(0, [
                    Validators.required
                ]),
                usingThisAuthority: new FormControl(false, [
                    Validators.required
                ])
            }),
            edit: new FormGroup({
                currencyOperatingConditionIdList: new FormControl(
                    [],
                    [Validators.required]
                ),
                requiredOperatingConditionIdList: new FormControl([]),
                days: new FormControl(undefined),
                requiredFlights: new FormControl(0, [Validators.required]),
                requiredFlightTimeSeconds: new FormControl(0, [
                    Validators.required
                ]),
                usingThisAuthority: new FormControl(false, [
                    Validators.required
                ])
            })
        });
        this.fetchData();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    fetchData() {
        combineLatest([
            this.currencyService.findCurrencyConfiguration(
                this.personnelRegisterId,
                this.managingOrganisationId
            ),
            this.jurisdictionService.findOperatingConditionCategories(
                this.airspaceJurisdictionId
            )
        ])
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(([currencyConfig, operatingConditionCategories]) => {
                this.currencyConfigurations = currencyConfig;
                this.operatingConditions = operatingConditionCategories.reduce(
                    (acc, v) =>
                        acc.concat(
                            v.values.map(val => ({
                                type:
                                    this.operatingConditionsTypeLookup[
                                        val.type
                                    ] ?? 'Misc',
                                value:
                                    val.type ===
                                    OperatingCategoryValuesDtoObject.Type
                                        .RPA_MTOW
                                        ? `<${this.formatMTOWPipe.transform(
                                              val.value as number
                                          )}`
                                        : this.operatingConditionsValueLookup[
                                              val.value
                                          ],
                                id: val.id
                            }))
                        ),
                    []
                );
            })
            .add(this.workTracker.createTracker());
    }

    addConfig() {
        const value = this.formGroup.controls.add.value;
        const command: CreateCurrencyConfigurationCommand = {
            personnelRegisterId: this.personnelRegisterId,
            days: value.days,
            requiredFlights: value.requiredFlights,
            requiredFlightTimeSeconds: value.requiredFlightTimeSeconds,
            currencyOperatingConditionList:
                value.currencyOperatingConditionIdList,
            requiredOperatingConditionIdList:
                value.requiredOperatingConditionIdList,
            usingThisAuthority: value.usingThisAuthority
        };

        this.currencyService
            .createCurrencyConfiguration(command)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: value => {
                    this.logging.success(`Added configuration`);
                    this.formGroup.controls.add.reset();
                    this.fetchData();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error adding configuration: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    updateConfig(config: CurrencyConfigurationDto) {
        const value = this.formGroup.controls.edit.value;
        const command: UpdateCurrencyConfigurationCommand = {
            days: value.days,
            requiredFlights: value.requiredFlights,
            requiredFlightTimeSeconds: value.requiredFlightTimeSeconds,
            currencyOperatingConditionIdList:
                value.currencyOperatingConditionIdList,
            requiredOperatingConditionIdList:
                value.requiredOperatingConditionIdList,
            usingThisAuthority: value.usingThisAuthority
        };
        this.currencyService
            .updateCurrencyConfiguration(config.id, command)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: value => {
                    this.logging.success(`Updated configuration`);
                    this.formGroup.controls.edit.reset();
                    this.editingConfig = null;
                    this.fetchData();
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error updating configuration: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    showEditMode(config: CurrencyConfigurationDto) {
        const currencyOperatingConditionList =
            config.currencyOperatingConditionList.reduce(
                (acc, c) => acc.concat(c.values.map(v => v.id)),
                []
            );
        const requiredOperatingConditionList =
            config.requiredOperatingConditionList.reduce(
                (acc, c) => acc.concat(c.values.map(v => v.id)),
                []
            );
        this.formGroup.controls.edit.setValue({
            currencyOperatingConditionIdList: currencyOperatingConditionList,
            requiredOperatingConditionIdList: requiredOperatingConditionList,
            days: config.days,
            requiredFlights: config.requiredFlights,
            requiredFlightTimeSeconds: config.requiredFlightTimeSeconds,
            usingThisAuthority: config.usingThisAuthority
        });
        this.formGroup.updateValueAndValidity();
        this.editingConfig = config.id;
    }

    deleteCurrencyConfig(config: CurrencyConfigurationDto) {
        const doneWorking = this.workTracker.createTracker();
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Delete currency configuration',
                'Are you sure you wish to delete this currency configuration?',
                'Delete',
                () =>
                    firstValueFrom(
                        this.currencyService.deleteCurrencyConfiguration(
                            config.id
                        )
                    )
            )
            .then(
                () => {
                    this.fetchData();
                    doneWorking();
                },
                (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error deleting configuration: ${error.message}`
                    );
                    doneWorking();
                }
            )
            .catch(() => {
                doneWorking();
            });
    }

    cancelEditMode() {
        this.editingConfig = null;
        this.formGroup.controls.edit.reset();
    }

    typesGroup(item: OperatingCategoryValuesDtoOperatingCategoryDto) {
        return item.type;
    }
}
